import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, MessageSquare, Bug, FileText, Shield, Info, Heart } from 'lucide-react';
import ShareAppButton from '@/components/ShareAppButton';

const SupportAbout = () => {
  const navigate = useNavigate();
  const appVersion = '1.03';
  const supportEmail = 'kidquotesapp@gmail.com';

  const handleContactSupport = () => {
    const subject = encodeURIComponent('KidQuotes Support Request');
    const body = encodeURIComponent(`Hi KidQuotes Support,\n\nI need help with:\n\n\n---\nApp Version: ${appVersion}\nDevice: iOS\n`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const handleSendFeedback = () => {
    const subject = encodeURIComponent('KidQuotes Feedback');
    const body = encodeURIComponent(`Hi KidQuotes Team,\n\nI have the following feedback:\n\n\n---\nApp Version: ${appVersion}\n`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const handleReportBug = () => {
    const subject = encodeURIComponent('KidQuotes Bug Report');
    const body = encodeURIComponent(`Hi KidQuotes Team,\n\nI found a bug:\n\nSteps to reproduce:\n1. \n2. \n3. \n\nExpected behavior:\n\n\nActual behavior:\n\n\n---\nApp Version: ${appVersion}\nDevice: iOS\n`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy');
  };

  const handleTermsOfService = () => {
    navigate('/terms');
  };

  return (
    <Card>
      <CardHeader className="text-center sm:text-left">
        <CardTitle>Support & About</CardTitle>
        <CardDescription>
          Get help, send feedback, or learn more about KidQuotes
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Contact Support */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Mail className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Contact Support</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Get help with issues or questions
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleContactSupport}
              className="w-full sm:w-auto"
            >
              Email Support
            </Button>
          </div>

          <Separator />

          {/* Send Feedback */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <MessageSquare className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Send Feedback</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Share your ideas and suggestions
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendFeedback}
              className="w-full sm:w-auto"
            >
              Send Feedback
            </Button>
          </div>

          <Separator />

          {/* Report a Bug */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Bug className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Report a Bug</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Let us know about technical issues
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReportBug}
              className="w-full sm:w-auto"
            >
              Report Bug
            </Button>
          </div>

          <Separator />

          {/* Share KidQuotes */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Heart className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Share KidQuotes</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tell friends and family about the app
                </p>
              </div>
            </div>
            <ShareAppButton />
          </div>

          <Separator />

          {/* Privacy Policy */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <Shield className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Privacy Policy</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Learn how we protect your data
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrivacyPolicy}
              className="w-full sm:w-auto"
            >
              View Policy
            </Button>
          </div>

          <Separator />

          {/* Terms of Service */}
          <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-3">
            <div className="flex items-start gap-3 flex-1">
              <FileText className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">Terms of Service</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Read our terms and conditions
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTermsOfService}
              className="w-full sm:w-auto"
            >
              View Terms
            </Button>
          </div>

          <Separator />

          {/* App Version */}
          <div className="flex items-center sm:items-start justify-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-muted-foreground mt-1" />
              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium leading-tight">App Version</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Current version: {appVersion}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SupportAbout;
